import type { PaginatedQuery } from "@shared/types/list";
import { DEFAULT_PAGE_SIZE } from "@shared/types/list";
import { PRODUCT_PACKAGE_TYPE_MAP, PRODUCT_TYPE_MAP, PRODUCT_TYPE_OPTIONS } from "@shared/enums/product";
import { t } from "@shared/utils/i18n";
import type { UiTagType } from "@shared/components/UiTag/index.vue";

/** 配对弹窗：筛选 + 分页（warehouseSkuId 由 open 注入） */
export interface ProductPairingDialogQueryParam extends PaginatedQuery {
    warehouseSkuId: string;
    /** 产品类型，空=全部 → 请求 typeIn */
    type: number | "";
    /** 产品负责人，空=全部 → 请求 ownerUserIdIn */
    ownerUserId: string;
    /** 组合搜索：asin / sku1p */
    asin: string;
    sku1p: string;
}

export const DEFAULT_PRODUCT_PAIRING_DIALOG_QUERY: Omit<ProductPairingDialogQueryParam, "warehouseSkuId"> = {
    type: "",
    ownerUserId: "",
    asin: "",
    sku1p: "",
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
};

export const PRODUCT_PAIRING_DIALOG_TYPE_OPTIONS = PRODUCT_TYPE_OPTIONS.map(item => ({
    label: item.label,
    value: item.value,
}));

export const PRODUCT_PAIRING_DIALOG_SEARCH_OPTIONS = [
    { label: "ASIN", value: "asin" },
    { label: "1PSKU", value: "sku1p" },
];

export interface ProductPairingCandidateAsinApi {
    asin?: string;
    countryCode?: string;
    country_code?: string;
}

/**
 * POST /manage/warehouseProductMatch/candidateList → data.items[]
 * 字段对齐产品列表常见形态，兼容缺省
 */
export interface ProductPairingCandidateItemApi {
    productId: string;
    sku1p?: string;
    productName?: string;
    model?: string;
    category?: string;
    type?: number;
    typeLabel?: string;
    packageType?: number;
    packageTypeLabel?: string;
    imageUrl?: string;
    image?: string;
    asins?: ProductPairingCandidateAsinApi[];
}

export interface ProductPairingCandidateAsin {
    asin: string;
    countryCode: string;
}

export interface ProductPairingCandidateRow {
    id: string;
    productId: string;
    image: string;
    sku1p: string;
    productName: string;
    model: string;
    category: string;
    type: number;
    typeLabel: string;
    packageType: number;
    packageTypeLabel: string;
    asins: ProductPairingCandidateAsin[];
}

export function mapProductPairingCandidateItem(item: ProductPairingCandidateItemApi): ProductPairingCandidateRow {
    const productId = item.productId;
    const type = Number(item.type) || 0;
    const packageType = Number(item.packageType) || 0;
    return {
        id: productId,
        productId,
        image: item.imageUrl || item.image || "",
        sku1p: item.sku1p || "",
        productName: item.productName || "",
        model: item.model || "",
        category: item.category || "",
        type,
        typeLabel: item.typeLabel || PRODUCT_TYPE_MAP[type] || "",
        packageType,
        packageTypeLabel: item.packageTypeLabel || PRODUCT_PACKAGE_TYPE_MAP[packageType] || "",
        asins: (item.asins ?? [])
            .map(a => ({
                asin: a.asin || "",
                countryCode: a.countryCode || a.country_code || "",
            }))
            .filter(a => a.asin),
    };
}

export function getProductPairingCandidateTypeTagType(type: number): UiTagType {
    if (type === 2) return "info";
    if (type === 3) return "warning";
    return "neutral";
}

export function getProductPairingCandidateRowActions() {
    return [{ label: t("配对"), key: t("配对") }];
}
